import { memo } from 'react';

import { Text } from '@/components/core';
import {
  BicSwiftValidator,
  ConfidenceValidator,
  EmailValidator,
  findField,
  FreightContainerIdValidator,
  IbanValidator,
  LicensePlateValidator,
  NonEmptyValidator,
  PassportNumberValidator,
  PhoneNumberValidator,
  RegexValidator,
  TaxIdValidator,
  UrlValidator,
  VatIdValidator,
} from '@/components/ui';
import {
  VALIDATOR_BIC_SWIFT_FUNCTION_ID,
  VALIDATOR_CANNOT_BE_EMPTY_FUNCTION_ID,
  VALIDATOR_CONFIDENCE_FUNCTION_ID,
  VALIDATOR_EMAIL_FUNCTION_ID,
  VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID,
  VALIDATOR_IBAN_FUNCTION_ID,
  VALIDATOR_LICENSE_PLATE_FUNCTION_ID,
  VALIDATOR_LOGEX_FUNCTION_ID,
  VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID,
  VALIDATOR_PHONE_NUMBER_FUNCTION_ID,
  VALIDATOR_REGEX_FUNCTION_ID,
  VALIDATOR_TAX_ID_FUNCTION_ID,
  VALIDATOR_URL_FUNCTION_ID,
  VALIDATOR_VAT_ID_FUNCTION_ID,
} from '@/constants';
import { useModelConfigStore } from '@/store';

import { LogexValidator } from './LogexValidator';

export type ValidatorConfigurationProps = {
  fieldId: string;
  validatorId: string;
};

const IValidatorConfiguration = ({ fieldId, validatorId }: ValidatorConfigurationProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const validator = field?.validators?.find((v) => v.id === validatorId);

  if (!validator) return null;
  switch (validator.functionId) {
    case VALIDATOR_CONFIDENCE_FUNCTION_ID:
      return <ConfidenceValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_REGEX_FUNCTION_ID:
      return <RegexValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_LOGEX_FUNCTION_ID:
      return <LogexValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_CANNOT_BE_EMPTY_FUNCTION_ID:
      return <NonEmptyValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_IBAN_FUNCTION_ID:
      return <IbanValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_VAT_ID_FUNCTION_ID:
      return <VatIdValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_TAX_ID_FUNCTION_ID:
      return <TaxIdValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID:
      return <PassportNumberValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_LICENSE_PLATE_FUNCTION_ID:
      return <LicensePlateValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_PHONE_NUMBER_FUNCTION_ID:
      return <PhoneNumberValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_BIC_SWIFT_FUNCTION_ID:
      return <BicSwiftValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_EMAIL_FUNCTION_ID:
      return <EmailValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID:
      return <FreightContainerIdValidator fieldId={fieldId} validatorId={validatorId} />;
    case VALIDATOR_URL_FUNCTION_ID:
      return <UrlValidator fieldId={fieldId} validatorId={validatorId} />;
  }
  return <Text>Unrecognized validator</Text>;
};

export const ValidatorConfiguration = memo(IValidatorConfiguration) as typeof IValidatorConfiguration;
